import { FC, Fragment, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import CapsulePriceForm from './CapsulePriceForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { CapsulePriceFormType } from 'interfaces';
import { capsulePriceSchema } from 'validationSchema';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import { useCreateCapsulePriceMutation } from 'generated/graphql';
import {
  ADD_TEXT,
  HTTP_STATUS,
  ADD_CAPSULE_PRICE_TEXT,
  capsulePriceInitialValue,
  CAPSULE_PRICE_IS_CREATING_TEXT,
} from 'constants/index';

const Add: FC = (): JSX.Element => {
  const navigation = useNavigate();

  const methods = useForm<CapsulePriceFormType>({
    defaultValues: capsulePriceInitialValue,
    resolver: yupResolver(capsulePriceSchema),
  });

  const { handleSubmit } = methods;

  const [createCapsulePrice, { loading }] = useCreateCapsulePriceMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { createCapsulePrice } = data;
      const { response } = createCapsulePrice || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<CapsulePriceFormType> = async (data) => {
    const { capacity, density, price, productSize, supplier, capsuleType } = data || {};

    const { value: capsuleTypeId } = capsuleType || {};
    const { value: supplierId } = supplier || {};
    const { value: productSizeId } = productSize || {};
    const { value: densityId } = density || {};

    await createCapsulePrice({
      variables: {
        createCapsulePriceInput: {
          price: Number(price),
          capacity: Number(capacity),

          densityId,
          supplierId,
          productSizeId,
          capsuleTypeId,
        },
      },
    });
  };

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={flexCenterBetween}>
            <Typography variant="h5">{ADD_CAPSULE_PRICE_TEXT}</Typography>
            <Button variant="contained" color="primary" type="submit">
              {ADD_TEXT}
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item lg={6} md={12}>
              <CapsulePriceForm loading={loading} />
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <BackdropLoader open={loading} text={CAPSULE_PRICE_IS_CREATING_TEXT} />
    </Fragment>
  );
};

export default Add;
